import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, X, FileText, Image as ImageIcon, CheckCircle2,
    AlertTriangle, Sparkles, Bold, Italic, List as ListIcon, ListOrdered,
    Undo2, Redo2, Link2, ChevronDown, ChevronRight, Info,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { usePageMeta } from "../../components/Layout";
import {
    MILESTONES, STAGE_INDEX, RESEARCH_KINDS, TERMINAL_STATUSES,
    STAGE_AWAITING_RETRY,
    isActiveOp, isRetryScheduled, isBlockedDailyBudget,
    formatElapsed, computeOpElapsedSeconds,
    buildMilestoneTimeline,
} from "./feasibilityOpHelpers";
export {
    formatElapsed, computeOpElapsedSeconds, buildMilestoneTimeline,
} from "./feasibilityOpHelpers";

/**
 * Desktop Feasibility Study — Batch A page.
 *
 * Setup (State A) and an inert Report Editor shell (State B skeleton
 * — not reachable in Batch A because Generate Research is a Batch-B
 * seam). Persistence is deliberately structured; the eventual report
 * document is NOT an HTML string (v1.3 §18.5). This file establishes
 * the visual arrangement / component boundaries so Batches B and C
 * can slot in a proper structured editor without redesigning the page.
 */

// ============================================================
// Custom confirm modal (never native confirm/alert/prompt)
// ============================================================
function LrsModal({ open, title, children, onClose, footer, testid }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid={testid} onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
                 onClick={(e) => e.stopPropagation()}>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{title}</h3>
                <div className="text-sm text-gray-600 leading-relaxed">{children}</div>
                {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
            </div>
        </div>
    );
}

// ============================================================
// State A — Setup / Before Research
// ============================================================

function PropertyDetailsCard({ study, savingType, onTypeChange }) {
    const address = study?.project?.property_address
        || study?.project?.postcode || "—";
    return (
        <section className="bg-white rounded-lg border border-[#E5E2DC] p-6 shadow-sm mb-6"
                 data-testid="fs-property-details">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">
                Property Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Address
                    </label>
                    <input type="text" value={address} disabled
                           data-testid="fs-property-address"
                           className="w-full bg-gray-50 border border-gray-200 text-gray-600 text-sm rounded px-3 py-2 cursor-not-allowed" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Property Type <span className="text-red-500">*</span>
                    </label>
                    <select
                        data-testid="fs-property-type"
                        value={study.property_type || ""}
                        onChange={(e) => onTypeChange(e.target.value)}
                        disabled={savingType}
                        className="w-full bg-white border border-gray-300 text-gray-900 text-sm rounded px-3 py-2 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none">
                        <option value="">Select property type…</option>
                        <optgroup label="HOUSE">
                            <option value="detached">Detached</option>
                            <option value="semi_detached">Semi-detached</option>
                            <option value="end_of_terrace">End-of-terrace</option>
                            <option value="mid_terrace">Mid-terrace</option>
                            <option value="bungalow">Bungalow</option>
                        </optgroup>
                        <optgroup label="OTHER RESIDENTIAL">
                            <option value="flat_apartment">Flat / apartment</option>
                            <option value="maisonette">Maisonette</option>
                            <option value="other">Other</option>
                        </optgroup>
                    </select>
                </div>
            </div>
        </section>
    );
}

const CATEGORY_LABELS = {
    extensions: "Extensions",
    loft_and_roof: "Loft & Roof",
    internal_conversion: "Internal / Conversion",
    external_alterations: "External Alterations",
    garden_frontage: "Garden & Frontage",
    services_plant: "Services / Plant",
    compliance: "Compliance",
    other: "Other",
};

function ProposedWorksCard({ study, taxonomy, onCommit }) {
    // Build a live map keyed by code. Selection = presence.
    const selectedMap = useMemo(() => {
        const m = new Map();
        for (const w of study.proposed_works || []) m.set(w.code, w);
        return m;
    }, [study.proposed_works]);
    const [otherDesc, setOtherDesc] = useState(study.other_description || "");
    useEffect(() => {
        setOtherDesc(study.other_description || "");
    }, [study.other_description]);

    // Group by category preserving spec order.
    const groups = useMemo(() => {
        const g = {};
        for (const item of taxonomy) {
            (g[item.category] = g[item.category] || []).push(item);
        }
        return g;
    }, [taxonomy]);

    const toggle = (code) => {
        const now = new Map(selectedMap);
        if (now.has(code)) now.delete(code);
        else now.set(code, { code, note: "" });
        onCommit({
            proposed_works: Array.from(now.values()).map(
                (w) => ({ code: w.code, note: w.note || null })),
        });
    };
    const setNote = (code, note) => {
        const now = new Map(selectedMap);
        if (!now.has(code)) return;
        now.set(code, { ...now.get(code), note });
        onCommit({
            proposed_works: Array.from(now.values()).map(
                (w) => ({ code: w.code, note: w.note || null })),
        });
    };
    const commitOtherDesc = () => {
        onCommit({ other_description: otherDesc || "" });
    };

    const otherSelected = selectedMap.has("other_proposed_works");

    return (
        <section className="bg-white rounded-lg border border-[#E5E2DC] p-6 shadow-sm mb-6"
                 data-testid="fs-proposed-works">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-2">
                <h2 className="text-lg font-semibold text-gray-900">Proposed Works</h2>
                <span className="text-xs text-gray-500">Select works and add optional notes</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                {Object.entries(groups).filter(([cat]) => cat !== "other").map(([cat, items]) => (
                    <div key={cat} data-testid={`fs-works-group-${cat}`}>
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                            {CATEGORY_LABELS[cat] || cat}
                        </h3>
                        <div className="space-y-2">
                            {items.map((it) => {
                                const isSel = selectedMap.has(it.code);
                                const entry = selectedMap.get(it.code);
                                return (
                                    <label key={it.code} className="block">
                                        <div className="flex items-start">
                                            <input type="checkbox" checked={isSel}
                                                onChange={() => toggle(it.code)}
                                                data-testid={`fs-work-toggle-${it.code}`}
                                                className="mt-1 mr-2 rounded border-gray-300 text-gray-800 focus:ring-gray-800 h-4 w-4" />
                                            <span className="text-sm text-gray-800">{it.label}</span>
                                        </div>
                                        {isSel && (
                                            <input type="text"
                                                data-testid={`fs-work-note-${it.code}`}
                                                defaultValue={entry?.note || ""}
                                                onBlur={(e) => {
                                                    const v = e.target.value;
                                                    if (v !== (entry?.note || "")) setNote(it.code, v);
                                                }}
                                                placeholder="Add optional note…"
                                                className="mt-1.5 ml-6 w-[calc(100%-1.5rem)] bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-gray-400 placeholder-gray-400" />
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            {/* Other — spans full width; requires description when selected. */}
            <div className="mt-6 pt-4 border-t border-gray-100" data-testid="fs-works-group-other">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                    Other
                </h3>
                <label className="block">
                    <div className="flex items-start">
                        <input type="checkbox" checked={otherSelected}
                            onChange={() => toggle("other_proposed_works")}
                            data-testid="fs-work-toggle-other_proposed_works"
                            className="mt-1 mr-2 rounded border-gray-300 text-gray-800 focus:ring-gray-800 h-4 w-4" />
                        <span className="text-sm text-gray-800">Other proposed works</span>
                    </div>
                    {otherSelected && (
                        <>
                            <textarea rows={2}
                                data-testid="fs-other-description"
                                value={otherDesc}
                                onChange={(e) => setOtherDesc(e.target.value)}
                                onBlur={commitOtherDesc}
                                placeholder="Describe the other proposed works (required)…"
                                className={`mt-1.5 ml-6 w-[calc(100%-1.5rem)] bg-gray-50 border rounded px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-gray-400 ${
                                    !otherDesc.trim() ? "border-red-300" : "border-gray-200"
                                }`} />
                            {!otherDesc.trim() && (
                                <div className="ml-6 mt-1 text-[11px] text-red-600"
                                     data-testid="fs-other-required">
                                    A description is required when Other is selected.
                                </div>
                            )}
                        </>
                    )}
                </label>
            </div>
        </section>
    );
}

function PropertyImagesCard({ study, onChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [pendingLabel, setPendingLabel] = useState("front");
    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("label", pendingLabel);
            fd.append("upload", file);
            const r = await api.post(
                `/admin/projects/${study.project_id}/feasibility-study/property-images`,
                fd);
            onChange(r.data);
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };
    const remove = async (item_id) => {
        try {
            const r = await api.delete(
                `/admin/projects/${study.project_id}/feasibility-study/property-images/${item_id}`);
            onChange(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Remove failed");
        }
    };
    const images = study.property_images || [];
    return (
        <section className="bg-white rounded-lg border border-[#E5E2DC] p-6 shadow-sm"
                 data-testid="fs-property-images">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Property Images</h2>
                <div className="flex items-center gap-2">
                    <select value={pendingLabel} onChange={(e) => setPendingLabel(e.target.value)}
                        data-testid="fs-image-label"
                        className="text-xs border border-gray-200 rounded px-2 py-1 bg-white">
                        <option value="front">Front</option>
                        <option value="rear">Rear</option>
                        <option value="other">Other</option>
                    </select>
                    <button type="button" disabled={uploading}
                        onClick={() => inputRef.current?.click()}
                        data-testid="fs-image-upload-btn"
                        className="text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1 rounded bg-gray-50 disabled:opacity-50">
                        {uploading ? "Uploading…" : "Upload"}
                    </button>
                    <input ref={inputRef} type="file" accept="image/*" className="hidden"
                        onChange={(e) => upload(e.target.files?.[0])} />
                </div>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="fs-image-list">
                {images.length === 0 && (
                    <div className="text-xs text-gray-400 italic">No images yet.</div>
                )}
                {images.map((img) => (
                    <div key={img.id}
                        data-testid={`fs-image-${img.id}`}
                        className="w-24 h-24 bg-gray-100 rounded border border-gray-200 flex flex-col items-center justify-center relative group">
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                        <span className="text-[10px] text-gray-500 mt-1 uppercase">{img.label}</span>
                        <button type="button" onClick={() => remove(img.id)}
                            data-testid={`fs-image-remove-${img.id}`}
                            className="absolute inset-0 bg-black/40 hidden group-hover:flex items-center justify-center rounded cursor-pointer">
                            <span className="text-white text-xs">Remove</span>
                        </button>
                    </div>
                ))}
            </div>
        </section>
    );
}

function SupportingFilesCard({ study, onChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("upload", file);
            const r = await api.post(
                `/admin/projects/${study.project_id}/feasibility-study/supporting-files`,
                fd);
            onChange(r.data);
            toast.success("File uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };
    const remove = async (item_id) => {
        try {
            const r = await api.delete(
                `/admin/projects/${study.project_id}/feasibility-study/supporting-files/${item_id}`);
            onChange(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Remove failed");
        }
    };
    const files = study.supporting_files || [];
    return (
        <section className="bg-white rounded-lg border border-[#E5E2DC] p-6 shadow-sm"
                 data-testid="fs-supporting-files">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Supporting Information</h2>
                <button type="button" disabled={uploading}
                    onClick={() => inputRef.current?.click()}
                    data-testid="fs-supporting-upload-btn"
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1 rounded bg-gray-50 disabled:opacity-50">
                    {uploading ? "Uploading…" : "Upload File"}
                </button>
                <input ref={inputRef} type="file" className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])} />
            </div>
            <ul className="space-y-2 text-sm" data-testid="fs-supporting-list">
                {files.length === 0 && (
                    <li className="text-xs text-gray-400 italic">No supporting files yet.</li>
                )}
                {files.map((f) => (
                    <li key={f.id}
                        data-testid={`fs-supporting-${f.id}`}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 border border-transparent hover:border-gray-100 rounded">
                        <div className="flex items-center text-gray-700 min-w-0">
                            <FileText className="w-4 h-4 mr-2 text-gray-400 shrink-0" />
                            <span className="truncate">{f.filename || "(unnamed file)"}</span>
                        </div>
                        <button type="button" onClick={() => remove(f.id)}
                            data-testid={`fs-supporting-remove-${f.id}`}
                            className="text-gray-400 hover:text-red-500 shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function AdditionalResearchNotesCard({ study, onCommit }) {
    const [val, setVal] = useState(study.additional_research_notes || "");
    useEffect(() => {
        setVal(study.additional_research_notes || "");
    }, [study.additional_research_notes]);
    return (
        <section className="bg-white rounded-lg border border-[#E5E2DC] p-6 shadow-sm mb-6"
                 data-testid="fs-additional-notes">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Additional Research Notes</h2>
            <p className="text-sm text-gray-500 mb-3">
                Optional context or instructions for the eventual Researcher.
            </p>
            <textarea rows={4} value={val}
                data-testid="fs-additional-notes-input"
                onChange={(e) => setVal(e.target.value)}
                onBlur={() => {
                    if (val !== (study.additional_research_notes || "")) {
                        onCommit({ additional_research_notes: val });
                    }
                }}
                placeholder="e.g. Pay particular attention to recent applications at nos. 43 and 47."
                className="w-full bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-gray-400 min-h-[80px]" />
        </section>
    );
}

function ReadinessCard({ study, onGenerate, generating }) {
    const r = study.readiness || {};
    const ready = !!r.research_ready;
    const reasons = r.blocking_reasons || [];
    const reasonLabels = {
        missing_address_or_postcode: "Add the project address or postcode.",
        missing_property_type: "Select a Property Type.",
        no_proposed_works_selected: "Select at least one Proposed Works item.",
        missing_other_description: "Add a description for Other proposed works.",
    };
    return (
        <section className="bg-white rounded-lg border border-[#E5E2DC] p-6 shadow-sm flex items-center justify-between"
                 data-testid="fs-readiness">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Research Readiness</h2>
                {ready ? (
                    <p className="text-sm text-green-600 flex items-center mt-1"
                       data-testid="fs-readiness-ready">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Minimum requirements satisfied
                    </p>
                ) : (
                    <ul className="mt-1 space-y-0.5"
                        data-testid="fs-readiness-blocked">
                        {reasons.map((r) => (
                            <li key={r} className="text-sm text-amber-700 flex items-center">
                                <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" />
                                {reasonLabels[r] || r}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <button type="button" onClick={onGenerate}
                disabled={!ready || generating}
                data-testid="fs-generate-research"
                className="bg-[#333333] hover:bg-[#1a1a1a] text-white px-6 py-2.5 rounded font-medium shadow-sm transition-colors flex items-center disabled:opacity-40 disabled:cursor-not-allowed">
                <Sparkles className="w-4 h-4 mr-2" />
                {generating ? "…" : "GENERATE RESEARCH"}
            </button>
        </section>
    );
}

// ============================================================
// State B — Report Editor SHELL (Batch A skeleton only)
// ============================================================
//
// Deliberately inert. Batch B populates `study.current_research`.
// Batch C replaces the placeholder with the production structured
// editor. The persistence contract intentionally does NOT store an
// HTML string; this shell renders whatever structured content the
// backend supplies once available.
//
// Rendered only when `study.current_research` is present (never in
// Batch A). Kept in this file so the visual arrangement — canvas +
// toolbar + right-hand AI Review panel — is available to Batch B/C
// without redesigning the page.

function EditorToolbarShell() {
    const btn = "p-1.5 text-gray-400 rounded transition-colors";
    return (
        <div className="sticky top-0 z-20 bg-white border border-gray-200 shadow-sm rounded-t-lg w-full max-w-[21cm] px-2 py-2 flex items-center space-x-1 border-b-0"
             data-testid="fs-editor-toolbar">
            <select disabled
                    className="text-sm border-none bg-transparent rounded px-2 py-1 text-gray-500 outline-none w-32 cursor-not-allowed">
                <option>Normal text</option>
                <option>Heading 1</option>
                <option>Heading 2</option>
                <option>Heading 3</option>
            </select>
            <div className="w-px h-5 bg-gray-300 mx-1"></div>
            <button disabled className={btn} title="Bold"><Bold className="w-4 h-4" /></button>
            <button disabled className={btn} title="Italic"><Italic className="w-4 h-4" /></button>
            <div className="w-px h-5 bg-gray-300 mx-1"></div>
            <button disabled className={btn} title="Bullet list"><ListIcon className="w-4 h-4" /></button>
            <button disabled className={btn} title="Numbered list"><ListOrdered className="w-4 h-4" /></button>
            <div className="w-px h-5 bg-gray-300 mx-1"></div>
            <button disabled className={btn} title="Link"><Link2 className="w-4 h-4" /></button>
            <button disabled className={btn + " flex items-center text-xs"} title="Insert image">
                <ImageIcon className="w-4 h-4 mr-1" /> Insert
            </button>
            <div className="flex-1"></div>
            <button disabled className={btn} title="Undo"><Undo2 className="w-4 h-4" /></button>
            <button disabled className={btn} title="Redo"><Redo2 className="w-4 h-4" /></button>
        </div>
    );
}

function ReportEditorShell({ study }) {
    // Batch A: no research yet. This is defensive rendering only.
    return (
        <div className="h-full flex flex-col relative pb-10"
             data-testid="fs-editor-state">
            <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded border border-gray-200 inline-flex items-center">
                    Report editor (skeleton) — populated by Batch B
                </div>
                <div className="flex space-x-2">
                    <button disabled className="bg-white border border-gray-300 text-gray-400 px-4 py-2 rounded text-sm font-medium cursor-not-allowed">REVIEW RESEARCH</button>
                    <button disabled className="bg-white border border-gray-300 text-gray-400 px-4 py-2 rounded text-sm font-medium cursor-not-allowed">REFINE RESEARCH</button>
                    <button disabled className="bg-white border border-gray-300 text-gray-400 px-4 py-2 rounded text-sm font-medium cursor-not-allowed">RESEARCH AGAIN</button>
                    <button disabled className="bg-[#333333] text-white/80 px-4 py-2 rounded text-sm font-medium ml-4 cursor-not-allowed">APPROVE REPORT</button>
                </div>
            </div>
            <div className="flex-1 flex flex-row items-start gap-6 min-h-0 relative">
                <div className="flex-1 flex flex-col items-center overflow-y-visible">
                    <EditorToolbarShell />
                    <div className="bg-white w-full max-w-[21cm] min-h-[29.7cm] shadow-md border border-gray-300 rounded-b-lg p-16 mb-12 text-gray-400 text-sm italic flex items-center justify-center"
                         data-testid="fs-editor-canvas">
                        The Feasibility Study report will appear here after Batch B generates it.
                    </div>
                </div>
                <aside className="w-[320px] shrink-0 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-[calc(100vh-260px)] sticky top-0"
                       data-testid="fs-review-panel">
                    <div className="p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                        <h3 className="font-semibold text-gray-800 flex items-center">
                            <Sparkles className="w-4 h-4 mr-2 text-[#d92c2c]" />
                            AI Research Review
                        </h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 text-sm text-gray-400 italic">
                        Review findings appear here once the independent review runs (Batch B).
                    </div>
                </aside>
            </div>
        </div>
    );
}

// ============================================================
// Truthful async-operation helpers (addendum §B–§G)
// ============================================================
//
// The backend operation row is the source of truth.  Everything the
// dialog shows — status label, milestone timeline, elapsed timer,
// terminal outcome — is derived from what the poll returns.  No
// percent bars, no fake narration, no chain-of-thought.
//
// The pure logic lives in `feasibilityOpHelpers.js` so focused Jest
// coverage can exercise it without pulling in the react-router-dom
// v7 ESM tree.

function formatNextRetry(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        // Local HH:MM is enough — no time zone label needed.
        return d.toLocaleTimeString([], {
            hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
}

function StatusLabel({ op }) {
    if (!op) return null;
    if (op.status === "succeeded") return "Research generated.";
    if (op.status === "failed_permanent") {
        return "Research could not be completed.";
    }
    if (isBlockedDailyBudget(op)) {
        return "Waiting for daily provider budget reset (00:00 UTC).";
    }
    // Transient rate-limit / provider-capacity wait — truthful copy,
    // no invented ETA.  Includes the scheduled next-attempt time and
    // the retry count so an admin can gauge how close to terminal.
    if (isRetryScheduled(op) && op.stage === STAGE_AWAITING_RETRY) {
        const when = formatNextRetry(op.next_retry_at);
        const attempt = (op.retry_count || 0) + 1;
        return `Provider rate limit reached — retrying shortly${
            when ? ` (next attempt at ${when})` : ""}. `
            + `Attempt ${attempt}.`;
    }
    if (op.status === "failed") {
        // Transient failure without an awaiting-retry stage (e.g.
        // internal error).  Still non-terminal but no useful stage.
        return "Retrying shortly…";
    }
    if (op.status === "pending") return "Research queued…";
    // Processing — surface the current stage in plain English.
    const idx = STAGE_INDEX[op.stage] ?? -1;
    if (idx < 0) return "Running live research…";
    return MILESTONES[idx].label + "…";
}

// Compact k-formatting for token counts in the diagnostics row.
function fmtKTokens(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    if (v < 1000) return String(v);
    return `${(v / 1000).toFixed(v >= 100_000 ? 0 : 1)}k`;
}

function fmtPct(used, cap) {
    if (!used || !cap) return "";
    const pct = Math.round((Number(used) / Number(cap)) * 100);
    return ` (${pct}%)`;
}

function fmtRelativeFuture(iso) {
    if (!iso) return "";
    const then = Date.parse(iso);
    if (Number.isNaN(then)) return "";
    const s = Math.max(0, Math.round((then - Date.now()) / 1000));
    if (s <= 0) return "any moment";
    if (s < 60) return `in ${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `in ${m}m ${String(rem).padStart(2, "0")}s`;
}

// Operator-only diagnostics.  Collapsed by default; surfaces the
// data the ops API now returns (retry_count, heartbeat_age_seconds,
// next_retry_at, error_class, budget_snapshot).  Deliberately
// discreet — this is not primary UI.
function OpDiagnostics({ op }) {
    const [open, setOpen] = useState(false);
    if (!op) return null;
    const hbAge = op.heartbeat_age_seconds;
    const hbColour = hbAge == null
        ? "text-gray-500"
        : hbAge < 30 ? "text-green-600"
            : hbAge < 90 ? "text-amber-600"
                : "text-red-600";
    const snap = op.budget_snapshot || null;
    const attempt = (op.retry_count || 0) + 1;
    return (
        <div className="mt-4 border-t border-gray-100 pt-3"
             data-testid="fs-generate-diagnostics">
            <button type="button" onClick={() => setOpen((v) => !v)}
                className="text-xs uppercase tracking-wide text-gray-500
                            hover:text-gray-800 flex items-center gap-1"
                data-testid="fs-generate-diagnostics-toggle">
                {open ? <ChevronDown className="w-3 h-3" />
                      : <ChevronRight className="w-3 h-3" />}
                Diagnostics
            </button>
            {open && (
                <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs"
                    data-testid="fs-generate-diagnostics-body">
                    <div className="flex justify-between"
                         data-testid="fs-diag-attempt">
                        <dt className="text-gray-500">Attempt</dt>
                        <dd className="text-gray-800">{attempt}</dd>
                    </div>
                    <div className="flex justify-between"
                         data-testid="fs-diag-heartbeat">
                        <dt className="text-gray-500">Heartbeat</dt>
                        <dd className={hbColour}>
                            {hbAge == null ? "—" : `${hbAge}s ago`}
                        </dd>
                    </div>
                    {op.next_retry_at && (
                        <div className="flex justify-between"
                             data-testid="fs-diag-next-retry">
                            <dt className="text-gray-500">Next retry</dt>
                            <dd className="text-gray-800">
                                {fmtRelativeFuture(op.next_retry_at)}
                            </dd>
                        </div>
                    )}
                    {op.error_class && (
                        <div className="flex justify-between"
                             data-testid="fs-diag-error-class">
                            <dt className="text-gray-500">Error class</dt>
                            <dd className="text-gray-800 font-mono">
                                {op.error_class}
                            </dd>
                        </div>
                    )}
                    {snap && (
                        <div className="flex justify-between col-span-2"
                             data-testid="fs-diag-daily-budget">
                            <dt className="text-gray-500">Daily budget</dt>
                            <dd className="text-gray-800">
                                {fmtKTokens(snap.tpd_used_today)} /
                                {" "}{fmtKTokens(snap.tpd_limit)} used
                                {fmtPct(snap.tpd_used_today, snap.tpd_limit)}
                            </dd>
                        </div>
                    )}
                    {op.last_error_detail && (
                        <SchemaValidationDetail
                            detail={op.last_error_detail} />
                    )}
                </dl>
            )}
        </div>
    );
}

function SchemaValidationDetail({ detail }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="col-span-2 mt-1"
             data-testid="fs-diag-technical-details">
            <button type="button" onClick={() => setOpen((v) => !v)}
                data-testid="fs-diag-technical-details-toggle"
                className="text-xs text-gray-500 hover:text-gray-800
                            flex items-center gap-1">
                {open ? <ChevronDown className="w-3 h-3" />
                      : <ChevronRight className="w-3 h-3" />}
                Show technical details
            </button>
            {open && (
                <pre className="mt-2 text-[11px] leading-tight
                                  font-mono bg-gray-50 border
                                  border-gray-200 rounded p-2
                                  max-h-64 overflow-auto whitespace-pre-wrap
                                  break-words"
                     data-testid="fs-diag-technical-details-body">
                    {detail}
                </pre>
            )}
        </div>
    );
}

function MilestoneTimeline({ op }) {
    const items = buildMilestoneTimeline(op);
    return (
        <ul className="space-y-1.5" data-testid="fs-generate-timeline">
            {items.map((m) => (
                <li key={m.stage} className="flex items-center text-sm"
                    data-testid={`fs-generate-milestone-${m.stage}`}
                    data-state={m.state}>
                    <span aria-hidden="true"
                          className={
                              m.state === "done"
                                  ? "inline-block w-4 h-4 mr-2 text-green-600"
                                  : m.state === "running"
                                      ? "inline-block w-4 h-4 mr-2 text-amber-500"
                                      : "inline-block w-4 h-4 mr-2 text-gray-300"
                          }>
                        {m.state === "done"
                            ? "✓"
                            : m.state === "running"
                                ? "●"
                                : "○"}
                    </span>
                    <span className={m.state === "pending"
                        ? "text-gray-400"
                        : "text-gray-800"}>{m.label}</span>
                </li>
            ))}
        </ul>
    );
}

// Truthful controller for a single async operation.  Encapsulates
// poll lifecycle, 1-second local elapsed tick, backend-derived
// timestamps, terminal handling, and 409 re-attachment so Review /
// Refine / Research Again can reuse the exact same pattern later
// without duplication (addendum §H).
export function useOperationController({ projectId, onSucceeded }) {
    const [op, setOp] = useState(null);
    const [tickNow, setTickNow] = useState(() => Date.now());
    const [errorBanner, setErrorBanner] = useState("");
    const pollRef = useRef(null);
    const tickRef = useRef(null);
    const opIdRef = useRef(null);

    const stop = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    useEffect(() => () => stop(), [stop]);

    // Poll a specific operation id.  Continues polling that exact
    // id — never switches to "latest wins".
    const attach = useCallback((opId, seedOp) => {
        if (!opId) return;
        opIdRef.current = opId;
        setErrorBanner("");
        if (seedOp) setOp(seedOp);
        stop();

        const tickFn = () => setTickNow(Date.now());
        tickRef.current = setInterval(tickFn, 1000);
        tickFn();

        const pollFn = async () => {
            try {
                const r = await api.get(
                    `/admin/projects/${projectId}`
                    + `/feasibility-study/operations/${opId}`);
                const fresh = r?.data?.operation;
                if (!fresh) return;
                setOp(fresh);
                if (TERMINAL_STATUSES.has(fresh.status)) {
                    stop();
                    if (fresh.status === "succeeded") {
                        if (typeof onSucceeded === "function") {
                            try { await onSucceeded(fresh); }
                            catch { /* onSucceeded failures are non-fatal */ }
                        }
                    }
                }
            } catch {
                // Transient poll failure — next tick retries.  The
                // op is safe on the backend regardless of what the
                // client observes.
            }
        };
        pollFn();
        pollRef.current = setInterval(pollFn, 3000);
    }, [projectId, stop, onSucceeded]);

    // Clear state entirely — used when the user dismisses a
    // completed dialog.  Does NOT weaken the backend's active-op
    // uniqueness guard.
    const reset = useCallback(() => {
        stop();
        setOp(null);
        setErrorBanner("");
        opIdRef.current = null;
    }, [stop]);

    // Trigger a new operation.  If the backend replies 409 with
    // `existing_operation_id`, re-attach rather than dead-ending in
    // the confirmation modal (addendum §B.2).
    const start = useCallback(async ({ path, body }) => {
        setErrorBanner("");
        try {
            const r = await api.post(
                `/admin/projects/${projectId}/feasibility-study/${path}`,
                body || undefined);
            const fresh = r?.data?.operation;
            if (!fresh?.id) throw new Error("no operation id returned");
            attach(fresh.id, fresh);
            return { ok: true, op: fresh };
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409 && detail?.existing_operation_id) {
                // Existing active op — re-attach and stay in the
                // running-state UI.
                attach(detail.existing_operation_id);
                return { ok: true, reattached: true };
            }
            if (detail && typeof detail === "object" && detail.reasons) {
                setErrorBanner("Cannot start research: "
                    + detail.reasons.join(", "));
            } else if (typeof detail === "string") {
                setErrorBanner(detail);
            } else if (detail && typeof detail === "object"
                        && detail.message) {
                setErrorBanner(detail.message);
            } else {
                setErrorBanner("Research could not be started.");
            }
            return { ok: false };
        }
    }, [projectId, attach]);

    const elapsedSeconds = useMemo(
        () => computeOpElapsedSeconds(op, tickNow),
        [op, tickNow]);

    return {
        op, elapsedSeconds, errorBanner,
        isRunning: isActiveOp(op),
        isTerminal: op && TERMINAL_STATUSES.has(op.status),
        attach, start, reset, stop,
    };
}

function AbbreviatedFingerprint({ value, testid }) {
    if (!value) return <span data-testid={testid}>—</span>;
    const head = value.slice(0, 8);
    const tail = value.slice(-4);
    return <span className="font-mono" data-testid={testid} title={value}>
        {head}…{tail}
    </span>;
}

// Admin-only Run Details panel shown alongside a completed research
// operation.  Deliberately kept separate from the client-facing
// report content (addendum §F).  Never exposes API keys, auth
// headers, secrets, full prompts, chain-of-thought, hidden
// reasoning, or raw stack traces.
export function RunDetailsPanel({ op, study }) {
    const [open, setOpen] = useState(true);
    if (!op) return null;
    const research = study?.current_research || null;
    const output = research?.output || null;
    const evidenceCount = Array.isArray(output?.evidence_register)
        ? output.evidence_register.length : null;
    const assessed = Array.isArray(output?.per_work_assessments)
        ? output.per_work_assessments.length : null;
    const expectedWorks = Array.isArray(study?.proposed_works)
        ? study.proposed_works.length : null;
    const retrievedSources = op.retrieved_sources || [];
    const unsupported = op.unsupported_inputs || [];
    const startedMs = op.started_at ? Date.parse(op.started_at) : null;
    const finishedMs = op.finished_at ? Date.parse(op.finished_at) : null;
    const totalDurationS = (startedMs && finishedMs)
        ? Math.max(0, Math.floor((finishedMs - startedMs) / 1000))
        : null;
    const failed = op.status === "failed" || op.status === "failed_permanent";
    const completenessOutcome = failed
        ? "Failed"
        : op.status === "succeeded" ? "Passed" : "Pending";
    return (
        <section className="mt-8 bg-white border border-[#E5E2DC] rounded-lg shadow-sm"
                 data-testid="fs-run-details-panel">
            <button type="button"
                data-testid="fs-run-details-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-3 text-left">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Info className="w-4 h-4 text-gray-500" />
                    Run details
                    <span className="ml-2 text-xs font-normal text-gray-500 uppercase tracking-wide">
                        Admin only
                    </span>
                </div>
                {open ? <ChevronDown className="w-4 h-4 text-gray-500" />
                      : <ChevronRight className="w-4 h-4 text-gray-500" />}
            </button>
            {open && (
                <div className="px-5 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm"
                     data-testid="fs-run-details-body">
                    <RunDetailRow label="Operation type"
                        value={op.kind} testid="rd-kind" />
                    <RunDetailRow label="Status"
                        value={op.status} testid="rd-status" />
                    <RunDetailRow label="Operation ID"
                        value={<span className="font-mono">{op.id}</span>}
                        testid="rd-id" />
                    <RunDetailRow label="Started"
                        value={op.started_at || "—"} testid="rd-started" />
                    <RunDetailRow label="Finished"
                        value={op.finished_at || "—"} testid="rd-finished" />
                    <RunDetailRow label="Total duration"
                        value={totalDurationS != null
                            ? formatElapsed(totalDurationS) : "—"}
                        testid="rd-duration" />
                    <RunDetailRow label="Provider"
                        value={op.provider || "—"} testid="rd-provider" />
                    <RunDetailRow label="Model"
                        value={op.model || "—"} testid="rd-model" />
                    <RunDetailRow label="Reasoning effort"
                        value={op.reasoning_effort || "—"}
                        testid="rd-reasoning" />
                    <RunDetailRow label="Provider request ID"
                        value={op.provider_request_id
                            ? <span className="font-mono">{op.provider_request_id}</span>
                            : "—"}
                        testid="rd-provider-req" />
                    <RunDetailRow label="Input tokens"
                        value={op.input_tokens ?? "—"}
                        testid="rd-input-tokens" />
                    <RunDetailRow label="Output tokens"
                        value={op.output_tokens ?? "—"}
                        testid="rd-output-tokens" />
                    <RunDetailRow label="Retrieved source count"
                        value={retrievedSources.length}
                        testid="rd-source-count" />
                    <RunDetailRow label="Evidence item count"
                        value={evidenceCount ?? "—"}
                        testid="rd-evidence-count" />
                    <RunDetailRow label="Proposed works assessed"
                        value={assessed != null && expectedWorks != null
                            ? `${assessed} / ${expectedWorks}`
                            : (assessed ?? "—")}
                        testid="rd-works-assessed" />
                    <RunDetailRow label="Completeness validation"
                        value={completenessOutcome}
                        testid="rd-completeness" />
                    <RunDetailRow label="Retry count"
                        value={op.retry_count ?? 0}
                        testid="rd-retries" />
                    <RunDetailRow label="Unsupported attachments"
                        value={unsupported.length === 0
                            ? "None"
                            : `${unsupported.length} skipped`}
                        testid="rd-unsupported" />
                    <RunDetailRow label="Input fingerprint"
                        value={<AbbreviatedFingerprint
                                    value={op.input_fingerprint}
                                    testid="rd-fingerprint-value" />}
                        testid="rd-fingerprint" />
                    <RunDetailRow label="Research created"
                        value={research?.generated_at
                            || study?.research_generated_at || "—"}
                        testid="rd-research-created" />
                    {failed && (
                        <RunDetailRow label="Error class"
                            value={op.error_class || "—"}
                            testid="rd-error-class" />
                    )}
                    {failed && op.last_error && (
                        <RunDetailRow label="Diagnostic"
                            value={<span className="text-red-700">{op.last_error}</span>}
                            testid="rd-error-detail" />
                    )}
                </div>
            )}
        </section>
    );
}

function RunDetailRow({ label, value, testid }) {
    return (
        <div className="flex justify-between py-1.5 border-b border-gray-50 last:border-b-0"
             data-testid={testid}>
            <span className="text-gray-500">{label}</span>
            <span className="text-gray-800 text-right ml-4 break-all"
                  data-testid={`${testid}-value`}>
                {value == null || value === "" ? "—" : value}
            </span>
        </div>
    );
}

// ============================================================
// Main page
// ============================================================
export default function FeasibilityStudy() {
    const { id: projectId } = useParams();
    const navigate = useNavigate();
    const [study, setStudy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [genOpen, setGenOpen] = useState(false);
    // Most-recent research-family operation — used to render the
    // admin-only Run details panel alongside a completed study
    // (addendum §F+§G).  Retained across dialog close/re-open.
    const [latestResearchOp, setLatestResearchOp] = useState(null);
    const projectLabel = study?.client_name || study?.project?.client_name || "Project";
    usePageMeta({ title: `Feasibility · ${projectLabel} | Studio G4 Admin` });

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await api.get(`/admin/projects/${projectId}/feasibility-study`);
            setStudy(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load feasibility study");
        } finally { setLoading(false); }
    }, [projectId]);
    useEffect(() => { load(); }, [load]);

    const commit = useCallback(async (partial) => {
        setSaving(true);
        try {
            const r = await api.put(
                `/admin/projects/${projectId}/feasibility-study`, partial);
            setStudy(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    }, [projectId]);

    // Truthful operation controller — backend is source of truth.
    const runOp = useOperationController({
        projectId,
        onSucceeded: async () => { await load(); },
    });

    // Page-load / hard-refresh recovery (addendum §B.1 + §B.4 + §I.3):
    // if a research-family operation is already non-terminal on the
    // backend, re-attach to it and restore the running dialog rather
    // than treating the visit as a fresh trigger.  Also picks up the
    // most recent completed research op so the Run details panel is
    // populated across refreshes (addendum §G).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(
                    `/admin/projects/${projectId}/feasibility-study/operations`);
                if (cancelled) return;
                const ops = r?.data?.operations || [];
                const research = ops.filter(
                    (o) => RESEARCH_KINDS.has(o.kind));
                const active = research.find(isActiveOp);
                if (active) {
                    runOp.attach(active.id, active);
                    setGenOpen(true);
                }
                const lastTerminal = research.find(
                    (o) => TERMINAL_STATUSES.has(o.status));
                if (lastTerminal) setLatestResearchOp(lastTerminal);
            } catch {
                // Missing operations endpoint or transient error —
                // fall back to normal setup UI.
            }
        })();
        return () => { cancelled = true; };
        // Only on project change / initial mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // When a running op terminates, keep the completed op available
    // for the Run details panel and freeze the dialog's contents
    // until the user closes it.
    useEffect(() => {
        if (runOp.op && TERMINAL_STATUSES.has(runOp.op.status)
                && RESEARCH_KINDS.has(runOp.op.kind)) {
            setLatestResearchOp(runOp.op);
        }
    }, [runOp.op]);

    const kickResearch = async () => {
        await runOp.start({ path: "generate-research" });
    };

    const closeDialog = () => {
        // The backend row is authoritative — closing the dialog is
        // purely a UI action.  Only wipe the controller state once
        // the op is terminal, otherwise the next page interaction
        // could look like there's no active op even though the
        // backend still holds one (addendum §B.4).
        if (runOp.isTerminal || !runOp.op) {
            runOp.reset();
        }
        setGenOpen(false);
    };

    if (loading) return (
        <div className="p-8 text-sm text-gray-500" data-testid="fs-loading">Loading…</div>
    );
    if (error) return (
        <div className="p-8 text-sm text-red-600" data-testid="fs-error">{error}</div>
    );
    if (!study) return null;

    const taxonomy = study.taxonomy?.proposed_works || [];
    const hasResearch = !!study.current_research;
    const address = study.project?.property_address
        || study.project?.postcode || "—";
    // Generate button is disabled when the backend has an active
    // research-family op OR while the current start-post is in
    // flight (addendum §B.1: bind to backend state, not local state).
    const generateDisabled = runOp.isRunning;

    // Header status pill: "Research generated" only appears once the
    // backend research is committed AND there is no active new-op
    // running (addendum §I.6, §I.7).
    const showResearchGeneratedPill = hasResearch && !runOp.isRunning;

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden bg-[#f7f4ee]"
             data-testid="feasibility-study-page">
            <header className="pt-8 px-10 pb-6 shrink-0 border-b border-[#e0e0e0] bg-[#f7f4ee] z-10">
                <button type="button"
                    onClick={() => navigate(`/admin/projects/${projectId}`)}
                    data-testid="fs-back-to-workspace"
                    className="flex items-center text-gray-500 hover:text-gray-800 text-sm font-medium transition-colors mb-4">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back to workspace
                </button>
                <div className="flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">
                            Initial Feasibility Study
                        </h1>
                        <p className="text-lg text-gray-600 mt-1" data-testid="fs-header-address">{address}</p>
                    </div>
                    <div className="flex items-center text-sm text-gray-500"
                         data-testid="fs-header-state">
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                            showResearchGeneratedPill ? "bg-green-500"
                                : runOp.isRunning ? "bg-amber-500"
                                    : "bg-yellow-400"
                        }`}></span>
                        {showResearchGeneratedPill ? "Research generated"
                            : runOp.isRunning ? "Research running…"
                                : "Draft setup"}
                    </div>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto px-10 py-8 scroll-smooth">
                {!hasResearch ? (
                    <div className="max-w-4xl mx-auto pb-20" data-testid="fs-setup-state">
                        <PropertyDetailsCard
                            study={study} savingType={saving}
                            onTypeChange={(v) => commit({ property_type: v || null })} />
                        <ProposedWorksCard
                            study={study} taxonomy={taxonomy}
                            onCommit={commit} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <PropertyImagesCard study={study} onChange={setStudy} />
                            <SupportingFilesCard study={study} onChange={setStudy} />
                        </div>
                        <AdditionalResearchNotesCard
                            study={study} onCommit={commit} />
                        {runOp.isRunning && (
                            <div className="mb-3 flex items-center justify-between
                                              bg-amber-50 border border-amber-200
                                              rounded-lg px-4 py-2.5 text-sm
                                              text-amber-900"
                                 data-testid="fs-running-pill">
                                <div className="flex items-center gap-2">
                                    <span className="inline-block h-2 w-2
                                                      rounded-full bg-amber-500
                                                      animate-pulse"
                                          aria-hidden="true" />
                                    <span>
                                        Research running ·{" "}
                                        <span data-testid="fs-running-pill-elapsed">
                                            {formatElapsed(runOp.elapsedSeconds)}
                                        </span>
                                    </span>
                                </div>
                                <button type="button"
                                    onClick={() => setGenOpen(true)}
                                    data-testid="fs-view-progress"
                                    className="text-amber-900 hover:text-amber-950
                                                 underline font-medium">
                                    View progress
                                </button>
                            </div>
                        )}
                        <ReadinessCard study={study}
                            generating={generateDisabled}
                            onGenerate={() => setGenOpen(true)} />
                    </div>
                ) : (
                    <div className="max-w-[1400px] mx-auto pb-20">
                        <ReportEditorShell study={study} />
                        {latestResearchOp && (
                            <RunDetailsPanel
                                op={latestResearchOp} study={study} />
                        )}
                    </div>
                )}
            </div>

            <LrsModal open={genOpen}
                testid="fs-generate-modal"
                title={runOp.isRunning ? "Generating research…"
                    : runOp.op && runOp.op.status === "succeeded"
                        ? "Research generated"
                        : runOp.op && runOp.op.status === "failed_permanent"
                            ? "Research failed"
                            : "Generate research?"}
                onClose={() => closeDialog()}
                footer={
                    <>
                        <button type="button"
                            onClick={closeDialog}
                            data-testid="fs-generate-cancel"
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded">
                            Close
                        </button>
                        {!runOp.op && (
                            <button type="button" onClick={kickResearch}
                                data-testid="fs-generate-confirm"
                                className="bg-[#333333] hover:bg-[#1a1a1a] text-white px-5 py-2 rounded text-sm font-medium">
                                Run
                            </button>
                        )}
                    </>
                }>
                {!runOp.op && (
                    <p>
                        The Researcher will investigate the property,
                        planning context, policy and precedents for the
                        selected proposed works and produce a structured
                        evidence record. Your inputs and uploads are
                        already saved.
                    </p>
                )}
                {runOp.op && (
                    <div className="space-y-4" data-testid="fs-generate-status">
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                            Current status
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#333]">
                            {runOp.isRunning && !isBlockedDailyBudget(runOp.op) && (
                                <span className="inline-block h-2 w-2
                                                  rounded-full bg-amber-500
                                                  animate-pulse"
                                        aria-hidden="true" />
                            )}
                            <span data-testid="fs-generate-status-label">
                                <StatusLabel op={runOp.op} />
                            </span>
                        </div>
                        {isBlockedDailyBudget(runOp.op) && (
                            <div className="p-3 rounded text-sm border
                                              bg-blue-50 border-blue-200
                                              text-blue-900"
                                 data-testid="fs-generate-budget-block">
                                <div className="font-semibold mb-1">
                                    Daily OpenAI budget reached
                                    {runOp.op.budget_snapshot
                                        ? ` (${fmtKTokens(runOp.op.budget_snapshot.tpd_used_today)} of ${fmtKTokens(runOp.op.budget_snapshot.tpd_limit)} used).`
                                        : "."}
                                </div>
                                <div>
                                    This job will resume automatically after
                                    00:00 UTC. No action needed.
                                </div>
                            </div>
                        )}
                        {!isBlockedDailyBudget(runOp.op) && (
                            <div className="text-xs text-[#666]"
                                    data-testid="fs-generate-elapsed">
                                Elapsed: {formatElapsed(runOp.elapsedSeconds)}
                            </div>
                        )}
                        <div>
                            <div className="text-xs uppercase tracking-wide
                                              text-gray-500 mb-2">
                                Timeline
                            </div>
                            <MilestoneTimeline op={runOp.op} />
                        </div>
                        <div className="text-xs text-[#888] pt-2 border-t border-gray-100">
                            Operation id:{" "}
                            <span className="font-mono"
                                    data-testid="fs-generate-opid">
                                {runOp.op.id}
                            </span>
                        </div>
                        <OpDiagnostics op={runOp.op} />
                    </div>
                )}
                {runOp.errorBanner && (
                    <div className="mt-4 p-3 rounded text-sm border
                                    bg-amber-50 border-amber-200 text-amber-800"
                         data-testid="fs-generate-msg">
                        {runOp.errorBanner}
                    </div>
                )}
                {runOp.op && runOp.op.status === "failed_permanent" && (
                    <div className="mt-4 p-3 rounded text-sm border
                                    bg-red-50 border-red-200 text-red-800"
                         data-testid="fs-generate-failure">
                        Research failed ({runOp.op.error_class || "error"}).
                        {" "}{runOp.op.last_error || ""}
                    </div>
                )}
                {isRetryScheduled(runOp.op) && (
                    <div className="mt-4 p-3 rounded text-sm border
                                    bg-amber-50 border-amber-200 text-amber-800"
                         data-testid="fs-generate-retry-notice">
                        Provider capacity is currently rate-limited.
                        The next attempt is scheduled automatically —
                        no action needed.
                        {" "}Retry count: {runOp.op.retry_count || 0}.
                    </div>
                )}
            </LrsModal>
        </div>
    );
}
